<template>
    <div :class="['appt-confirm-card', !draft.canCreate && 'is-blocked']">
        <div class="card-header">
            <span class="card-icon" aria-hidden="true">📅</span>
            <div class="card-title">
                <div class="title-main">
                    <span v-if="total > 1" class="appt-seq">{{ index }}/{{ total }}</span>
                    {{ headerDate }}
                </div>
                <div class="title-sub">{{ timeRange }} ({{ draft.durationMinutes }} dk)</div>
            </div>
        </div>

        <!-- Block banner (CanCreate=false) -->
        <div v-if="!draft.canCreate && draft.blockReason" class="card-block">
            <span class="block-icon" aria-hidden="true">⚠</span>
            <span class="block-text">{{ draft.blockReason }}</span>
        </div>

        <!-- Fields -->
        <dl class="card-fields">
            <div class="field-row">
                <dt>Hizmet</dt>
                <dd>{{ draft.serviceName }}</dd>
            </div>
            <div class="field-row">
                <dt>Personel</dt>
                <dd>{{ draft.staffName }}</dd>
            </div>
            <div v-if="draft.roomName" class="field-row">
                <dt>Oda</dt>
                <dd>{{ draft.roomName }}</dd>
            </div>
            <!--
                Paketten seans düşen randevu: bu bilgi ÖNCEDEN yalnızca asistanın cevap
                metninde geçiyordu; model cümleyi atladığında (ölçüldü — gpt-5.6-luna'da
                sıkça) onaylayan personel seansın düştüğünü hiçbir yerde göremiyordu.
                Taslak zaten packageName taşıyor → deterministik olarak burada gösterilir,
                modelin anlatmasına bağlı kalmaz.
            -->
            <div v-if="draft.packageName" class="field-row field-package">
                <dt>Paket</dt>
                <dd>{{ draft.packageName }} <span class="pkg-note">— 1 seans düşecek</span></dd>
            </div>
        </dl>

        <!-- Actions -->
        <div class="card-actions">
            <button
                type="button"
                class="btn btn-primary"
                :disabled="!draft.canCreate || saving"
                @click="onConfirm">
                <span v-if="!saving">✓ Oluştur</span>
                <span v-else class="spinner" aria-hidden="true"></span>
            </button>
            <button
                type="button"
                class="btn btn-secondary"
                :disabled="saving"
                @click="$emit('edit')">
                ✏ Düzenle
            </button>
            <button
                type="button"
                class="btn-ghost"
                :disabled="saving"
                @click="$emit('cancel')">
                İptal
            </button>
        </div>

        <!-- Success / error feedback -->
        <div v-if="errorMsg" class="card-error">
            <span class="error-icon" aria-hidden="true">!</span>
            <span>{{ errorMsg }}</span>
        </div>
    </div>
</template>

<script>
    import axios from '../../services/index';

    const DAY_NAMES = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
    const MONTH_NAMES = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
                         'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

    function pad2(n) { return String(n).padStart(2, '0'); }

    export default {
        name: 'AppointmentConfirmCard',
        props: {
            draft: { type: Object, required: true },
            index: { type: Number, default: 0 },
            total: { type: Number, default: 1 }
        },
        data() {
            return {
                saving: false,
                errorMsg: ''
            };
        },
        mounted() {
            // Enter ile onaylama
            this._onKey = (e) => {
                if (e.key !== 'Enter') return;
                if (e.target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
                if (!this.draft || !this.draft.canCreate || this.saving) return;
                e.preventDefault();
                this.onConfirm();
            };
            document.addEventListener('keydown', this._onKey);
        },
        beforeUnmount() {
            if (this._onKey) document.removeEventListener('keydown', this._onKey);
        },
        computed: {
            startLocal() { return new Date(this.draft.startLocal); },
            endLocal() { return new Date(this.draft.endLocal); },
            headerDate() {
                const d = this.startLocal;
                if (isNaN(d.getTime())) return '';
                return `${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()} ${DAY_NAMES[d.getDay()]}`;
            },
            timeRange() {
                const s = this.startLocal;
                const e = this.endLocal;
                if (isNaN(s.getTime()) || isNaN(e.getTime())) return '';
                return `${pad2(s.getHours())}:${pad2(s.getMinutes())} – ${pad2(e.getHours())}:${pad2(e.getMinutes())}`;
            }
        },
        methods: {
            async onConfirm() {
                if (!this.draft.canCreate || this.saving) return;
                this.saving = true;
                this.errorMsg = '';
                try {
                    const res = await axios.post('/Appointment/New', this.draft.upsertPayload);
                    if (res && res.data && res.data.success !== false) {
                        this.$emit('confirm', res.data);
                    } else {
                        this.errorMsg = (res && res.data && res.data.message)
                            || 'Randevu oluşturulamadı.';
                    }
                } catch (err) {
                    const apiMsg = err && err.response && err.response.data && err.response.data.message;
                    this.errorMsg = apiMsg || 'Randevu oluşturulurken bir hata oluştu.';
                } finally {
                    this.saving = false;
                }
            }
        }
    };
</script>

<style scoped>
    .appt-confirm-card {
        margin-top: 10px;
        padding: 14px;
        border-radius: 14px;
        background: linear-gradient(135deg, rgba(238, 242, 255, 0.85) 0%, rgba(250, 245, 255, 0.85) 100%);
        border: 1px solid rgba(99, 102, 241, 0.20);
        box-shadow: 0 2px 10px rgba(99, 102, 241, 0.08);
    }
    .appt-confirm-card.is-blocked {
        background: linear-gradient(135deg, rgba(254, 226, 226, 0.55) 0%, rgba(255, 237, 213, 0.55) 100%);
        border-color: rgba(239, 68, 68, 0.30);
    }

    /* Header */
    .card-header {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 12px;
    }
    .card-icon {
        font-size: 22px;
        line-height: 1;
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.08));
    }
    .card-title { line-height: 1.25; }
    .title-main { font-size: var(--est-fs-body); font-weight: 600; color: #1e293b; }
    .appt-seq {
        display: inline-block;
        margin-right: 6px;
        padding: 1px 7px;
        border-radius: 999px;
        background: rgba(99, 102, 241, 0.14);
        color: #4338ca;
        font-size: var(--est-fs-badge);
        font-weight: 700;
        vertical-align: middle;
    }
    .title-sub { font-size: var(--est-fs-meta); color: #4338ca; font-variant-numeric: tabular-nums; margin-top: 2px; }

    /* Block banner */
    .card-block {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 10px;
        margin-bottom: 12px;
        background: rgba(254, 226, 226, 0.7);
        border: 1px solid rgba(239, 68, 68, 0.30);
        border-radius: 10px;
        font-size: var(--est-fs-meta);
        color: #991b1b;
    }
    .block-icon { font-size: 14px; }

    /* Fields */
    .card-fields {
        margin: 0 0 14px 0;
        padding: 0;
        display: grid;
        gap: 6px;
    }
    .field-row {
        display: flex;
        align-items: baseline;
        gap: 8px;
        font-size: var(--est-fs-meta);
    }
    .field-row dt {
        flex: 0 0 90px;
        color: #64748b;
        font-weight: 500;
    }
    .field-row dd {
        flex: 1;
        margin: 0;
        color: #1e293b;
        font-weight: 500;
    }
    /* Paket satırı: onaylamadan önce fark edilmeli — danışanın seans hakkı eksiliyor. */
    .field-package dd {
        color: #92400e;
    }
    .field-package .pkg-note {
        font-weight: 600;
    }

    /* Actions */
    .card-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }
    .btn-primary, .btn-secondary, .btn-ghost {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 7px 14px;
        border-radius: 10px;
        font-size: var(--est-fs-meta);
        font-weight: 600;
        cursor: pointer;
        transition: transform 0.12s ease, box-shadow 0.15s ease, opacity 0.15s ease;
    }
    .btn-primary:hover:not(:disabled) {
        transform: translateY(-1px);
    }

    .btn-ghost {
        border: 1px solid transparent;
        background: transparent;
        color: #64748b;
        border-color: rgba(100, 116, 139, 0.20);
    }
    .btn-ghost:hover:not(:disabled) {
        background: rgba(100, 116, 139, 0.08);
    }
    .btn-ghost:disabled { opacity: 0.4; cursor: not-allowed; }

    /* Spinner */
    .spinner {
        width: 13px;
        height: 13px;
        border: 2px solid rgba(255, 255, 255, 0.4);
        border-top-color: white;
        border-radius: 50%;
        animation: spin 0.7s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }

    /* Error */
    .card-error {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 10px;
        padding: 7px 10px;
        background: rgba(254, 226, 226, 0.65);
        border: 1px solid rgba(239, 68, 68, 0.25);
        border-radius: 8px;
        font-size: var(--est-fs-meta);
        color: #991b1b;
    }
    .error-icon {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #ef4444;
        color: white;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 10px;
        font-weight: 700;
        flex-shrink: 0;
    }

    /* Mobile */
    @media (max-width: 768px) {
        .appt-confirm-card { padding: 12px; }
        .field-row dt { flex-basis: 80px; font-size: var(--est-fs-meta); }
        .field-row dd { font-size: var(--est-fs-meta); }
        .btn-primary, .btn-secondary, .btn-ghost { font-size: var(--est-fs-meta); padding: 6px 12px; }
    }
</style>
